import { utcToZonedTime } from 'date-fns-tz'
import { RequestOptions } from 'mixpanel-browser'
import { v4 as uuidv4 } from 'uuid'

import {
  IS_PRODUCTION,
  SESSION_STORAGE_KEY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PROPERTIES,
  S4C_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { getISO8601DateWithTimezone } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'

import mixpanel from './mixpanel-browser'
import { APP_ID, APP_PLATFORM } from '../../common/constants/appConstants'
import { getHealthPlanName } from '../../features/healthPlan/data/healthPlanSelectors'
import { getOnboardUserFavoriteProviderRequestId } from '../../features/onboard/data/onboardSelectors'
import { UPDATE_MIXPANEL_ACTIVITY } from '../appGlobals/appGlobalsActions'
import { getDeviceUUID } from '../appGlobals/appGlobalsSelectors'
import { getCustomerConfigurationValue, getCustomerName, getCustomerProductType } from '../customer/customerSelectors'
import { getId, getUserCountryCategory, getUserCountryName } from '../user/userSelectors'

export const MIXPANEL_PAGE_LOCATION = {
  CARE_NAVIGATOR_ENTRY_POINT: 'CN entrypoint',
  CARE_NAVIGATOR_ENTRY_POINT_INTERNATIONAL: 'international CN entrypoint',
  CARE_NAVIGATOR_ENTRY_POINT_ASSERTIVE_TRIAGE: 'assertive triage CN entrypoint',
  LANGUAGE_MODAL: 'language modal',
}

export const MIXPANEL_EVENTS = {
  PAGE_LOAD: 'Page Load',
  BUTTON_PRESS: 'Button Press',
  CHAT_COUNTRY_DETECTION_FAILURE: 'Chat country detection failure',
  STATUS: 'Status',
  FILTER_CHANGE: 'Filter Change',
  LIVE_MESSAGE_PRIMER: 'view live messaging primer',
  COACHING_SESSION_MODALITY: 'view LM vs LV coaching',
  VIEW_MOBILE_APP_PROMO: 'view lyra messaging promo',
  DISMISS_MOBILE_APP_PROMO: 'dismiss lyra messaging promo',
  GET_MOBILE_APP_PROMO: 'get lyra messaging app',
  SEND_MESSAGE: 'send message',
  VIEW_SATISFACTION_SURVEY: 'view satisfaction survey',
  SUBMIT_ADDITIONAL_SATISFACTION_SURVEY_INFO: 'submit additional satisfaction survey feedback',
  VIEW_MICROSITE: 'view microsite landing page',
  CHAT_LIVE: 'click chat live',
  FOOTER_CREATE_ACCOUNT: 'click create account',
  BOOK_FOLLOW_UP_SESSION: 'book follow up session',
  SELECT_PRIMARY_NEED: 'Select Primary Need',
  VIEW_HPI_INELIGIBLE: 'HPI-only ineligible',
  VIEW_EAP_HPI_INELIGIBLE: 'EAP+HPI ineligible',
  VIEW_HPI_PENDING: 'HPI-only unresponsive',
  VIEW_ACTIVITIES_LIST: 'view activities list',
  CHOOSE_COUNTRY_FOR_ACCOUNT: 'Choose country for account',
  CLICK_DONT_SEE_YOUR_LANGUAGE: "Don't see your language",
  INITIAL_APPOINTMENT_BOOKING_COMPLETE: 'complete initial appointment booking',
  VIEW_CARE_NAVIGATOR_REQUEST_FORM_PAGE: 'view T1 CA consult request form',
  CLICK_EMAIL_CARE_NAVIGATOR: 'click email cn',
  CLICK_CARE_NAVIGATOR_PHONE_NUMBER: 'click cn phone contact',
  CLICK_CARE_NAVIGATOR_EMAIL: 'click email contact',
  CLICK_CONTACT_CARE_NAVIGATOR: 'click to contact cn',
  PROFILE_WITH_EDITABLE_NAME_FIELDS_PAGE: 'Profile - With editable name fields page',
  LOCKED_PROFILE_WITH_EDITABLE_NAME_FIELDS_PAGE: 'Locked Profile - With editable name fields page',
  LOCKED_PROFILE_WITH_LOCKED_FIELDS_PAGE: 'Locked Profile - With locked fields page',
  LOCKED_PROFILE_NAME_CHANGE_PAGE: 'Locked Profile - Name change page',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_FOR_LIFE_EVENT: 'Locked Profile - Name Change - Click for life event',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_CHILD_DEPENDENT:
    'Locked Profile - Name Change - Click care for child dependent',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_SELF: 'Locked Profile - Name Change - Click care for self',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_ADULT_DEPENDENT:
    'Locked Profile - Name Change - Click care for adult dependent',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_REASON_NOT_LISTED_ON_PAGE:
    'Locked Profile - Name Change - Click reason not listed on page',
  OPEN_MESSAGE_PROVIDER_ALTERNATE_TIMES_MODAL: 'Alternate Times Prompt Button Clicked',
  DISMISS_MESSAGE_PROVIDER_ALTERNATE_TIMES_MODAL: 'Alternate Times Modal Dismiss Button Clicked',
  SEND_PROVIDER_ALTERNATE_TIMES: 'Alternate Times Modal Send Button Clicked',
  PAYMENT_DUE_INFRACTIONS_MODAL_SHOWN: 'Payment due infractions modal shown',
  PAYMENT_DUE_LEARN_MORE: 'Payment due learn more',
  PAYMENT_DUE_ADD_PAYMENT_METHOD: 'Payment due add payment method',
  PAYMENT_DUE_CLOSE_MODAL: 'Payment due close modal',
  INFRACTIONS_PAYMENT_ENTERED: 'Infractions payment entered',
  CLICK_HEADER_LYRA_LOGO: 'click header lyra logo',
  VIEW_AUTO_ENROLLMENT_PATHWAYS: 'view auto enrollment pathways',
  REENGAGEMENT_EMAIL_ZERO_AVAILABILITY_MODAL: 'reengagement email zero availability modal',
  REENGAGEMENT_EMAIL_TIME_ELAPSED_MODAL: 'reengagement email time elapsed modal',
}

export const MIXPANEL_ACTION = {
  NEW_SEARCH: 'New Search',
  CONTACT_US: 'Contact Us',
  CREATE_ACCOUNT: 'Create Account',
  VERIFY_ACCOUNT: 'Verify Account',
  SEND_EMAIL_VERIFICATION: 'Send Email Verification',
  ACCOUNT_VERIFICATION_SUCCESS: 'Account Verification Success',
  ACCOUNT_VERIFICATION_FAILED: 'Account Verification Failed',
  CONTINUE: 'Continue',
  CONTINUE_SUCCESS: 'Continue Success',
  CONTINUE_FAILED: 'Continue Failed',
  GO_TO_MY_STRENGTH: 'Go To My Strength',
  GO_TO_FAQ: 'Go To FAQ',
  CONTACT_CARE_TEAM: 'Contact Care Navigator Team',
  CONTACT_CARE_TEAM_FROM_AUD: 'contact care navigator team from AUD',
  VIEW_WORKLIFE: 'View Worklife Services',
  SELECT_WORKLIFE: 'Select Worklife Service',
  UNSUBSCRIBE_OUTCOMES: 'Unsubscribe from assessment emails',
  UNSUBSCRIBE_PROMOTIONAL: 'Unsubscribe from promotional emails',
  /* ---------------NAV BAR----------------- */
  PROFILE: 'Profile',
  /* ---------------APPOINTMENT----------------- */
  ADD_TO_CALENDAR: 'Add To Calendar',
  EDIT_APPOINTMENT: 'Edit Appointment',
  CONFIRM_APPOINTMENT_CHANGE: 'Confirm Appointment Change',
  LIVE_VIDEO_SESSION: 'Live Video Session',
  PING_LYRA_THERAPY: 'ping lyra therapy to keep token alive',
  /* ---------------ONBOARD----------------- */
  PROVIDERS_MATCHED: 'Providers Matched',
  ZERO_PROVIDERS_MATCHED: 'Zero Providers Matched',
  ZERO_BIPOC_PROVIDERS_FOUND: 'zero provider results (BIPOC)',
  GO_TO_MYSTRENGTH: 'Go To Mystrength',
  GO_TO_DOD: 'Go to Doctor On Demand',
  NEXT: 'Next',
  SKIP: 'Skip',
  MENU: 'Menu',
  BACK: 'Back',
  ONSITE: 'Onsite',
  USER_SPECIFIED: 'User-Specified',
  END_STATE: 'End State',
  REMOVE_PROVIDER: 'Remove Provider',
  ADVANCED_SEARCH: 'Advanced Search',
  CCBT: 'CCBT',
  LIVE_VIDEO_THERAPY: 'Live Video Therapy',
  UNSUPPORTED_OPTION: 'Recommend Unsupported Medication Option',
  UNSUPPORTED_OPTION_ALTERNATIVE: 'Unsupported Medication - Select Additional Support Option',
  VIEW_LYRA_BENEFITS: 'Display Lyra Benefits Disclaimer',
  FIND_A_THERAPIST: 'Find a Therapist',
  REFILL_CONTROLLED_MEDS: 'Refill controlled medications',
  /* ------ONBOARD - STRESS MANAGEMENT------- */
  RECOMMEND_SMP: 'View SMP as the recommended option',
  FIND_A_STRESS_COACH: 'Find A Stress Coach',
  CLOSE_STRESS_EXPLANATION_MODAL: 'Close Stress Explanation Modal',
  GET_COACHES: 'Find My Stress Coach',
  LEARN_MORE: 'Learn More',
  BOOK_WITH_PROVIDER: 'Book',
  VIEW_FULL_PROFILE: 'View Full Profile',
  GO_BACK_TO_RECOMMENDATIONS: 'Back To Recommendations',
  VIEW_NEXT_CALENDAR: "View Next Coach's Calendar",
  SEND_DOWNLOAD_LINK: 'Send Download Link',
  BOOK_APPOINTMENT: 'Book Appointment',
  /* ---------PROVIDER RESULTS--------------- */
  SINGLE_PROVIDER: 'Single Provider',
  SUBMIT_EMAIL: 'Submit Email',
  MULTIPLE_PROVIDERS: 'Multiple Providers',
  NAVIGATOR: 'Navigator',
  VIEW_NEXT_AVAILABLE_APPOINTMENT: 'View Next Available Appointment',
  VIEW_SCHEDULE: 'View Schedule',
  COMPARE_AVAILABILITY: 'Compare Availability',
  CHECK_PROVIDER_AVAILABILITY: 'Check Provider Availability',
  DONE_CHECK_PROVIDER_AVAILABILITY: 'Done - Check Provider Availability',
  CHANGE_LOCATION: 'Change Location',
  GET_PROVIDER_CONTACT_DETAILS: 'Get Provider Contact Details',
  PROVIDER_DETAILS: 'Provider Details',
  SEE_ALL_MY_RECOMMENDATIONS: 'See All My Recommendations',
  VIEW_APPOINTMENT: 'View Appointment',
  CONTACT_FOR_AVAILABILITY: 'Contact for Availability',
  SEE_FULL_PROFILE: 'See Full Profile',
  /* ---------PROVIDER DETAILS---------------- */
  SEND_EMAIL: 'Send Email To Provider',
  SHOW_ADDITIONAL_PROVIDER_INFORMATION: 'Provider Profile - Show Additional Information',
  SHOW_PROVIDER_DETAILS: 'Provider Contact Card - Show Provider Details',
  /* ---------PROVIDER Schedule--------------- */
  SCHEDULE_APPOINTMENT: 'Schedule Appointment',
  CHANGE_DATE: 'Change Date',
  UPDATE_CONTACT: 'Update Contact',
  /* ---------------FORGOT PASSWORD----------------- */
  FORGOT_PASSWORD: 'Forgot Password',
  /* ---------------REQUEST RESET LINK----------------- */
  REQUEST_RESET_LINK: 'Request Reset Link',
  REQUEST_RESEND_RESET_LINK: 'Request Resend Reset Link',
  /* ---------------PROFILE----------------- */
  UPDATE_PROFILE_SUCCESS: 'Update Profile Success',
  UPDATE_PROFILE_FAILED: 'Update Profile Failed',
  EDIT_PROFILE_DATA_RETRIEVAL_FAILED: 'Profile Data Retrieval Failed',
  SUBMIT: 'Submit',
  /* ---------------FOOTER----------------- */
  CHAT: 'Chat',
  /* ---------------WORK-LIFE----------------- */
  GET_WORK_LIFE_SUPPORT: "Click 'Get Support' On Work-Life",
  /* ----------- FAQ --------------- */
  FAQ_PAGE_LOAD: 'FAQ page load',
  VIEW_FAQS: 'View FAQs',
  /* ----------- LYRA THERAPY --------------- */
  VIEW_ASSESSMENT: 'View Assessment',
  START_WORKSHEET: 'Start Worksheet',
  OPEN_WORKSHEET: 'Open Worksheet Overview',
  START_LESSON: 'Start Digital Lesson',
  CHANGE_SESSION_PERIOD: 'View Different Session Period',
  RESCHEDULE_SESSION: 'Reschedule Session',
  CANCEL_SESSION: 'Cancel Session',
  OPEN_ACTIVITY_ENTRY: 'Open Activity Entry from Message',
  SEND_MESSAGE: 'Send Message',
  CHECK_MESSAGES: 'Check for New Messages',
  OPEN_MESSAGE_URL: 'Open URL from Message',
  OPEN_GUIDE_LINK: 'open guide link',
  TOGGLE_MESSENGER: 'Toggle Messenger',
  INITIAL_APPOINTMENT_BOOKING: 'Initial Appointment Booking',
  // Contact Care Navigator
  CLICK_CARE_NAVIGATOR_CONTACT_OPTIONS: 'Click need something else',
}

export const MIXPANEL_USER_TYPE = {
  CLIENT: 'Client',
}

export const createScheduleTrackingData = ({ times, timeZone }: { times?: Array<string | null>; timeZone: string }) => {
  if (!times) {
    return {}
  }
  const availableDateTimes = times.filter((dateTime) => Boolean(dateTime))
  const calendarSlotsShown = availableDateTimes?.map((dateTime) => {
    if (!dateTime) {
      return
    }
    const date = utcToZonedTime(dateTime, timeZone)
    return getISO8601DateWithTimezone({
      date,
      timeZone,
    })
  })
  return {
    [S4C_PROPERTIES.NUM_CALENDAR_SLOTS]: availableDateTimes?.length,
    [S4C_PROPERTIES.CALENDAR_SLOTS_SHOWN]: calendarSlotsShown,
  }
}

let currentPage = ''

export function handleOnboardPageLoad(page: $TSFixMe, action = '') {
  return function (dispatch: $TSFixMe, getState: $TSFixMe) {
    // Fill in dispatch here
    // Handle on page load
    if (page) {
      currentPage = page
    }

    mixpanel.track(MIXPANEL_EVENTS.PAGE_LOAD, {
      page: page || currentPage,
      action,
      match_for: getState().getIn(['triageDemographics', 'matchFor']),
      [S4C_PROPERTIES.MATCHER_REQUEST_ID]: getOnboardUserFavoriteProviderRequestId(getState()),
      country_category: getUserCountryCategory(getState()),
    })
  }
}

export function trackOnboardEvent({ event, action = '', page, ...rest }: MixpanelEvent, options?: RequestOptions) {
  return function (dispatch: $TSFixMe, getState: $TSFixMe) {
    const customerName = getCustomerName(getState())
    const eventData = {
      page,
      action,
      match_for: getState().getIn(['triageDemographics', 'matchFor'], ''),
      [S4C_PROPERTIES.MATCHER_REQUEST_ID]: getOnboardUserFavoriteProviderRequestId(getState()),
      customer_id: customerName,
      country_category: getUserCountryCategory(getState()),
      ...rest,
    }

    const lyraId = getId(getState())
    ensureMixpanelIdentification(lyraId, customerName)
    mixpanel.track(event, eventData, options as any)
  }
}

export function setMixpanelPage(page: $TSFixMe) {
  currentPage = page
}

export function initSuperProperties({ page, event, eventProps }: $TSFixMe) {
  return function (dispatch: $TSFixMe, getState: $TSFixMe) {
    const userId = getState().getIn(['user', 'id'], '')
    let mixpanelEvent = MIXPANEL_EVENTS.PAGE_LOAD
    if (page) {
      currentPage = page
    }

    const superProps = {
      ...(userId && {
        lyra_id: userId,
        customer_id: getCustomerName(getState()),
      }),
      customer_product_type: getCustomerProductType(getState()),
      care_reference_id: eventProps?.care_reference_id,
    }

    if (event) {
      mixpanelEvent = event
    }

    mixpanel.register(superProps)
    // Handle on page load
    mixpanel.track(mixpanelEvent, { ...eventProps, page: currentPage })
  }
}

export interface MixpanelEvent {
  [key: string]: any
  event: string
  action?: string
  page?: string
  options?: Dict
}

export function trackEventWithObj({ event, action = '', page, options, ...rest }: MixpanelEvent) {
  return function (dispatch: $TSFixMe, getState: $TSFixMe) {
    const customerId = getCustomerName(getState())
    const eventData = {
      page,
      action,
      match_for: getState().getIn?.(['triageDemographics', 'matchFor'], ''),
      customer_id: customerId,
      registered_country: getUserCountryName(getState()),
      country_category: getUserCountryCategory(getState()),
      [COMMON_MIXPANEL_PROPERTIES.LD_DEVICE_UUID]: getDeviceUUID(getState()),
      [COMMON_MIXPANEL_PROPERTIES.TAB_ID]: getTabId(),
      [COMMON_MIXPANEL_PROPERTIES.CUSTOMER_PRODUCT_TYPE]: getCustomerProductType(getState()),
      [COMMON_MIXPANEL_PROPERTIES.CUSTOMER_CONFIGURATION]: getCustomerConfigurationValue(getState()),
      [S4C_PROPERTIES.HEALTH_PLAN_NAME]: getHealthPlanName(getState()),
      ...rest,
    }

    const lyraId = getId(getState())
    ensureMixpanelIdentification(lyraId, customerId)
    mixpanel.track(event, eventData, options as any)

    if (!IS_PRODUCTION) {
      dispatch({
        type: UPDATE_MIXPANEL_ACTIVITY,
        payload: {
          event,
          action,
          page,
          data: { ...eventData },
          timestamp: Date.now(),
        },
      })
    }
  }
}

function ensureMixpanelIdentification(lyraId: string | undefined, customerName: string) {
  if (lyraId) {
    // https://lyrahealth.atlassian.net/browse/LYRA-9308
    // Temporarially adding identify call here to attempt fixing the missing calls which causes mixpanel events to miss User ID property.
    // These calls should not trigger mixpanel API calls unless existing distinct_id is different from the id (lyraId) being set here.
    mixpanel.identify(lyraId)

    // register_once to make sure super props are set
    // it won't re-register if the prop value has not changed
    mixpanel.register_once({
      lyra_id: lyraId,
      customer_id: customerName,
    })
  } else {
    mixpanel.register_once({
      app_platform: APP_PLATFORM,
      app_id: APP_ID,
    })
  }
}

const getTabId = () => {
  let tabId = sessionStorage.getItem(SESSION_STORAGE_KEY.TAB_ID)
  if (!tabId) {
    tabId = uuidv4()
    sessionStorage.setItem(SESSION_STORAGE_KEY.TAB_ID, tabId)
  }
  return tabId
}
